import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Select from "react-select";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AddSupplierPayments() {
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [userRole, setUserRole] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [currentUser, setCurrentUser] = useState("");
  const [locations, setLocations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedSupplierName, setSelectedSupplierName] = useState(null);

  useEffect(() => {
    const fetchUserRoleAndOrganizations = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return toast.error("Authentication token missing");

        const userResponse = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserRole(userResponse.data.role);

        if (userResponse.data.role === "Main Admin") {
          const orgResponse = await axios.get(`${API_BASE_URL}/organizations`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setOrganizations(orgResponse.data.organizations || []);
        }

      } catch (error) {
        console.error("Error fetching user role or organizations:", error);
        toast.error("Error fetching data");
      }
    };

    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCurrentUser(response.data);

        if (response.data.role === "Main Admin") fetchAllLocations();
        else if (response.data.role === "Company Admin") fetchFilteredLocations();
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error("Failed to fetch user data.");
      }
    };

    const fetchAllLocations = async () => {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data.locations);
    };

    const fetchFilteredLocations = async () => {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/products/filtered-locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data.locations);
    };

    const fetchSuppliers = async () => {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/suppliers/get`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data.suppliers);
    };

    fetchUserRoleAndOrganizations();
    fetchCurrentUser();
    fetchSuppliers();
  }, [userRole]);

  const handleAddPayment = async () => {
    try {
        const token = localStorage.getItem("token");
        if (!token) return toast.error("Authentication token missing");

        if (!selectedSupplier || !amount) {
        return toast.error("Please fill all required fields.");
        }

        const newPayment = {
        payment_date: paymentDate,
        supplier_id: selectedSupplier,
        amount: parseFloat(amount),
        invoices: []
        };

        const response = await axios.post(`${API_BASE_URL}/supplierPayments/add`, newPayment, {
        headers: { Authorization: `Bearer ${token}` }
        });

        if (response.status === 201) {
        toast.success("Supplier payment added successfully");

        // ✅ Clear form fields after successful submission
        setAmount('');
        setPaymentDate(new Date().toISOString().split('T')[0]);
        setSelectedSupplier(null);
        setSelectedSupplierName(null);
        }
    } catch (error) {
        console.error("Error adding supplier payment:", error);
        toast.error("Failed to add supplier payment");
    }
  };

  const customSupplierIDSelect = () => (
    <Select
      options={suppliers}
      getOptionLabel={(e) => e.supplier_code}
      getOptionValue={(e) => e._id}
      isSearchable
      placeholder="Search by Supplier ID"
      className="w-full"
      value={suppliers.find(option => option._id === selectedSupplier) || null}
      onChange={(selectedOption) => {
        setSelectedSupplier(selectedOption?._id);
        setSelectedSupplierName(selectedOption?.supplier_name);
      }}
    />
  );

  const customSupplierNameSelect = () => (
    <Select
      options={suppliers}
      getOptionLabel={(e) => e.supplier_name}
      getOptionValue={(e) => e._id}
      isSearchable
      placeholder="Search by Supplier Name"
      className="w-full"
      value={suppliers.find(option => option.supplier_name === selectedSupplierName) || null}
      onChange={(selectedOption) => {
        setSelectedSupplier(selectedOption?._id);
        setSelectedSupplierName(selectedOption?.supplier_name);
      }}
    />
  );

  return (
    <div className="container mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6 mt-4">
        <h2 className="text-2xl font-bold text-green-700 mb-6">
          Add Supplier Payment
        </h2>

        <div className="grid grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-gray-700 mb-2">Supplier ID</label>
            {customSupplierIDSelect()}
          </div>
          <div>
            <label className="block text-gray-700 mb-2">Supplier Name</label>
            {customSupplierNameSelect()}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-gray-700 mb-2">Amount (LKR)</label>
            <input
              type="number"
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter payment amount"
              min="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2">Payment Date</label>
            <input
              type="date"
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
            />
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            className="bg-green-700 text-white px-6 py-3 rounded-lg hover:bg-blue-600"
            onClick={handleAddPayment}
          >
            Add Payment
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddSupplierPayments;
